import {
  forwardRef,
  useEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

const variants: Record<Variant, string> = {
  /*
    The primary button carries a top-lit inset highlight and a tinted shadow in its own accent
    colour. That pairing is most of what separates a button that looks pressed into the page from
    one that looks placed on top of it.
  */
  primary:
    'bg-accent text-on-accent hover:brightness-[1.08] active:brightness-95 ' +
    'shadow-[inset_0_1px_0_0_rgb(255_255_255/0.18),0_1px_2px_rgb(0_0_0/0.12),0_8px_20px_-8px_var(--accent)]',
  secondary: 'bg-raised text-body border border-strong hover:bg-sunken hover:border-strong',
  ghost: 'text-muted hover:bg-sunken hover:text-body',
  danger: 'bg-transparent text-danger border border-danger/35 hover:bg-danger/10',
};

const sizes: Record<Size, string> = {
  // Touch targets are at least 40px tall. Most of this site's traffic is thumbs on a phone, and a
  // 32px control is a control that gets mis-tapped.
  sm: 'h-10 px-3.5 text-sm gap-1.5 rounded-xl',
  md: 'h-11 px-4.5 text-sm gap-2 rounded-xl',
  lg: 'h-12 px-6 text-base gap-2 rounded-2xl',
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant = 'primary', size = 'md', loading, disabled, children, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(
        'press sheen inline-flex items-center justify-center font-medium whitespace-nowrap',
        'transition duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]',
        'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
        variants[variant],
        sizes[size],
        className,
      )}
      {...props}
    >
      {loading && (
        <span
          className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent"
          aria-hidden
        />
      )}
      {children}
    </button>
  );
});

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
}

/** A square, icon-only control. Separate from Button so the 40px target is not left to a caller. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { className, label, children, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      title={label}
      className={cn(
        'press flex size-10 shrink-0 items-center justify-center rounded-xl text-muted',
        'transition duration-300 hover:bg-sunken hover:text-body',
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
});

interface FieldProps {
  label?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  children: ReactNode;
  className?: string;
}

export function Field({ label, hint, error, required, children, className }: FieldProps) {
  return (
    <label className={cn('flex flex-col gap-1.5', className)}>
      {label && (
        <span className="text-sm font-medium text-body">
          {label}
          {required && <span className="text-danger"> *</span>}
        </span>
      )}
      {children}
      {/* Errors replace hints rather than stacking: two messages under one input is noise. */}
      {error ? (
        <span className="animate-fade-in-down text-xs text-danger">{error}</span>
      ) : hint ? (
        <span className="text-xs text-faint">{hint}</span>
      ) : null}
    </label>
  );
}

const controlStyles =
  'w-full h-11 rounded-xl border border-default bg-raised px-3.5 text-sm text-body ' +
  'placeholder:text-faint outline-none ' +
  'transition duration-200 ease-[cubic-bezier(0.16,1,0.3,1)] ' +
  'hover:border-strong ' +
  'focus:border-accent focus:ring-4 focus:ring-accent/15 ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...props }, ref) {
    return <input ref={ref} className={cn(controlStyles, className)} {...props} />;
  },
);

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, children, ...props }, ref) {
    return (
      <select
        ref={ref}
        className={cn(controlStyles, 'select-chevron cursor-pointer', className)}
        {...props}
      >
        {children}
      </select>
    );
  },
);

export function Textarea({ className, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={cn(controlStyles, 'h-auto min-h-32 py-2.5', className)} {...props} />;
}

export function Checkbox({
  label,
  className,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode }) {
  return (
    <label
      className={cn(
        'group flex cursor-pointer items-center gap-2.5 rounded-lg py-1.5 text-sm text-body',
        'transition hover:text-accent',
        className,
      )}
    >
      <input
        type="checkbox"
        className="size-4 shrink-0 cursor-pointer rounded border-strong accent-[var(--accent)]"
        {...props}
      />
      <span className="leading-snug">{label}</span>
    </label>
  );
}

/**
 * A switch for the handful of yes/no filters.
 *
 * A row of checkboxes reads as a form; a row of switches reads as settings, and "samo sa slikama"
 * is a setting. It is a real checkbox underneath so it keeps the keyboard and screen-reader
 * behaviour that a div would have to reimplement badly.
 */
export function Switch({
  label,
  checked,
  onChange,
  className,
}: {
  label: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  className?: string;
}) {
  return (
    <label
      className={cn(
        'flex cursor-pointer items-center justify-between gap-3 py-2 text-sm text-body',
        className,
      )}
    >
      <span className="leading-snug">{label}</span>
      <span className="relative inline-flex shrink-0">
        <input
          type="checkbox"
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
          className="peer sr-only"
        />
        <span
          aria-hidden
          className={cn(
            'block h-6 w-10 rounded-full transition duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]',
            'peer-focus-visible:ring-4 peer-focus-visible:ring-accent/25',
            checked ? 'bg-accent' : 'bg-sunken ring-1 ring-[var(--border-strong)] ring-inset',
          )}
        />
        <span
          aria-hidden
          className={cn(
            'absolute top-1 left-1 size-4 rounded-full bg-white shadow-sm',
            'transition duration-300 ease-[cubic-bezier(0.22,1.2,0.36,1)]',
            checked ? 'translate-x-4' : 'translate-x-0',
          )}
        />
      </span>
    </label>
  );
}

/** A removable pill. Used for every active filter above the results. */
export function Chip({
  children,
  onRemove,
  removeLabel,
  className,
}: {
  children: ReactNode;
  onRemove?: () => void;
  /**
   * What this chip stands for, woven into the remove button's accessible name. Without it a row of
   * twelve filters reads as twelve identical "Ukloni filter" buttons to a screen reader.
   */
  removeLabel?: string;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'animate-pop-in inline-flex items-center gap-1.5 rounded-full border border-accent/25 bg-accent-soft',
        'py-1 pr-1 pl-2.5 text-xs font-medium text-accent',
        className,
      )}
    >
      {children}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={removeLabel ? `Ukloni filter: ${removeLabel}` : 'Ukloni filter'}
          className="press flex size-5 items-center justify-center rounded-full transition hover:bg-accent hover:text-on-accent"
        >
          <X size={11} />
        </button>
      )}
    </span>
  );
}

/**
 * A pill that is either on or off.
 *
 * Used wherever a long list of independent options has to fit on a phone. Forty checkboxes in a
 * column is thirteen hundred pixels of scrolling; the same options as chips wrap into a few rows,
 * and each one is a target a thumb can actually hit. `aria-pressed` is what tells a screen reader
 * this is a toggle rather than a link.
 */
export function ToggleChip({
  label,
  active,
  onToggle,
}: {
  label: string;
  active: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={active}
      className={cn(
        'press rounded-full border px-3 py-1.5 text-[12.5px] font-medium transition duration-300',
        active
          ? 'border-transparent bg-accent text-on-accent shadow-sm'
          : 'border-default bg-raised text-muted hover:border-strong hover:text-body',
      )}
    >
      {label}
    </button>
  );
}

export function Badge({
  children,
  tone = 'neutral',
  className,
}: {
  children: ReactNode;
  tone?: 'neutral' | 'accent' | 'success' | 'danger' | 'glass';
  className?: string;
}) {
  const tones = {
    neutral: 'bg-sunken text-muted',
    accent: 'bg-accent-soft text-accent',
    success: 'bg-success/15 text-success',
    danger: 'bg-danger/15 text-danger',
    // For badges that sit on top of a photograph, where any opaque fill looks like a sticker.
    glass: 'bg-black/55 text-white backdrop-blur-md',
  };

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium',
        tones[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

export function Spinner({ className }: { className?: string }) {
  return (
    <span
      role="status"
      aria-label="Učitavanje"
      className={cn(
        'inline-block size-6 animate-spin rounded-full border-2 border-current/25 border-t-current',
        className,
      )}
    />
  );
}

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="animate-fade-in-up flex flex-col items-center justify-center gap-3 rounded-[var(--radius-panel)] border border-dashed border-default px-6 py-16 text-center">
      {icon && <div className="text-faint">{icon}</div>}
      <h3 className="text-lg font-semibold text-body">{title}</h3>
      {description && <p className="max-w-md text-sm text-muted">{description}</p>}
      {action && <div className="mt-1">{action}</div>}
    </div>
  );
}

/**
 * A modal panel that slides in from the bottom on phones and from the right on tablets up.
 *
 * It keeps rendering for the length of the exit animation after `open` goes false, because a panel
 * that vanishes the instant you dismiss it feels like a bug even when it is not.
 */
export function Sheet({
  open,
  onClose,
  title,
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const [mounted, setMounted] = useState(open);
  const [leaving, setLeaving] = useState(false);
  const panel = useRef<HTMLDivElement>(null);

  /*
    Drag to dismiss, on the bottom-sheet layout only.

    A sheet on a phone that can only be closed by finding a small × in the corner is a sheet that
    feels like a web page. The offset lives in state rather than a ref because it is what the panel
    is rendered at — but it is written straight to the element's transform during the drag, so the
    finger is never waiting on a render.
  */
  const dragState = useRef<{ startY: number; offset: number } | null>(null);
  const [dragging, setDragging] = useState(false);

  // Callers pass an inline arrow, so `onClose` has a fresh identity on every render of the page
  // around this sheet. Holding it in a ref keeps the effect below keyed on `open` alone — otherwise
  // it re-ran on every filter change and yanked focus back to the panel mid-interaction.
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  });

  useEffect(() => {
    if (open) {
      setMounted(true);
      setLeaving(false);
      return;
    }

    if (!mounted) {
      return;
    }

    setLeaving(true);
    const timer = window.setTimeout(() => {
      setMounted(false);
      setLeaving(false);
    }, 220);

    return () => window.clearTimeout(timer);
  }, [open, mounted]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeRef.current();
      }
    };

    // Locking the body stops the page behind the sheet scrolling under the visitor's finger, which
    // on iOS otherwise happens the moment the sheet's own list hits its end.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);

    panel.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (!mounted) {
    return null;
  }

  const startDrag = (event: React.PointerEvent) => {
    // Only from the header. Dragging from the body would fight the list the visitor is scrolling.
    dragState.current = { startY: event.clientY, offset: 0 };
    setDragging(true);
    (event.target as Element).setPointerCapture?.(event.pointerId);
  };

  const moveDrag = (event: React.PointerEvent) => {
    const state = dragState.current;
    if (!state || !panel.current) return;

    // Downward only: dragging up would tear the sheet off the bottom of the screen.
    const offset = Math.max(0, event.clientY - state.startY);
    state.offset = offset;

    panel.current.style.transform = `translate3d(0, ${offset}px, 0)`;
    panel.current.style.transition = 'none';
  };

  const endDrag = () => {
    const state = dragState.current;
    if (!state || !panel.current) return;

    dragState.current = null;
    setDragging(false);

    panel.current.style.transition = '';

    // A short flick or a long pull both count; anything less springs back. 110px is about a thumb's
    // comfortable travel, and below it the gesture reads as an accident.
    if (state.offset > 110) {
      onClose();
    }

    panel.current.style.transform = '';
  };

  /*
    Rendered into the body rather than in place. Every page sits inside an animated wrapper, and an
    element carrying any transform becomes the containing block for its fixed descendants — which
    would anchor this sheet to the top of the document instead of to the viewport.
  */
  return createPortal(
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label={title}>
      <div
        className={cn(
          'absolute inset-0 bg-black/55',
          leaving ? 'animate-fade-out' : 'animate-fade-in',
        )}
        onClick={onClose}
        aria-hidden
      />

      <div
        ref={panel}
        tabIndex={-1}
        className={cn(
          'absolute inset-x-0 bottom-0 flex max-h-[92dvh] flex-col rounded-t-[var(--radius-panel)] bg-raised outline-none',
          'shadow-float sm:inset-y-0 sm:right-0 sm:left-auto sm:w-[26rem] sm:max-h-none sm:rounded-t-none sm:rounded-l-[var(--radius-panel)]',
          'transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]',
          // The entry animation and a live drag both write `transform`, so they must never run at
          // the same time — while a finger is down, the animation is out of the way.
          dragging
            ? ''
            : leaving
              ? 'animate-slide-down sm:animate-slide-out-right'
              : 'animate-slide-up sm:animate-slide-in-right',
        )}
      >
        <div
          onPointerDown={startDrag}
          onPointerMove={moveDrag}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          className="relative flex touch-none items-center justify-between gap-3 border-b border-default px-4 py-3 sm:touch-auto"
        >
          {/* The grab handle only makes sense on the bottom-sheet layout — and now it grabs. */}
          <span
            aria-hidden
            className="absolute top-2 left-1/2 h-1 w-10 -translate-x-1/2 rounded-full bg-[var(--border-strong)] sm:hidden"
          />
          <h2 className="text-base font-semibold text-body">{title}</h2>
          <IconButton label="Zatvori" onClick={onClose} className="-mr-2">
            <X size={18} />
          </IconButton>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-3">{children}</div>

        {footer && (
          <div className="border-t border-default bg-raised px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
